import { useCallback, useEffect, useRef, useState } from 'react';

import { isEqual } from 'lodash';
import { isNullOrUndefined } from '@vy/utils';
import {
  getLocalStorageString,
  removeLocalStorageEntry,
  setLocalStorageString,
} from '../utils/local-storage';

export interface TableColumnsState {
  visibility: Record<string, boolean>;
  widths: Record<string, number>;
  order: string[];
}

interface UseTableColumnsPersistenceParams extends TableColumnsState {
  tableName: string;
}

interface TableColumnsPersistenceResult {
  loadedConfig: TableColumnsState | undefined;
}

function createLocalStorageKey(tableName: string): string {
  return `columns-config-${tableName}`;
}

function loadConfigFromLocalStorage(tableName: string): TableColumnsState | undefined {
  const key = createLocalStorageKey(tableName);
  const json = getLocalStorageString(key);

  if (!json) {
    return undefined;
  }

  try {
    return JSON.parse(json) as TableColumnsState;
  } catch {
    removeLocalStorageEntry(key);
    return undefined;
  }
}

export function getTableColumnsConfig(tableName: string): TableColumnsState | undefined {
  return loadConfigFromLocalStorage(tableName);
}

function saveConfigToLocalStorage(tableName: string, config: TableColumnsState): void {
  const key = createLocalStorageKey(tableName);
  setLocalStorageString(key, JSON.stringify(config));
}

export function useTableColumnsPersistence({
  tableName,
  visibility,
  widths,
  order,
}: UseTableColumnsPersistenceParams): TableColumnsPersistenceResult {
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  const [loadedConfig, setLoadedConfig] = useState<TableColumnsState | undefined>();
  const loadedConfigRef = useRef<TableColumnsState | null>(null);

  useEffect(() => {
    const config = loadConfigFromLocalStorage(tableName);
    setLoadedConfig(config);
    loadedConfigRef.current = config ?? null;
    setIsConfigLoaded(true);
  }, [tableName]);

  const saveConfig = useCallback(() => {
    const config: TableColumnsState = { visibility, widths, order };
    saveConfigToLocalStorage(tableName, config);

    loadedConfigRef.current = config;
  }, [tableName, visibility, widths, order]);

  const hasConfigChanged = useCallback(() => {
    const loadedColumns = loadedConfigRef.current;
    if (isNullOrUndefined(loadedColumns)) {
      return true;
    }

    const hasVisibilityChanged =
      Object.keys(loadedColumns.visibility).length !== Object.keys(visibility).length ||
      Object.keys(loadedColumns.visibility).some((key) => loadedColumns.visibility[key] !== visibility[key]);
    const hasWidthsChanged =
      Object.keys(loadedColumns.widths).length !== Object.keys(widths).length ||
      Object.keys(loadedColumns.widths).some((key) => loadedColumns.widths[key] !== widths[key]);
    const hasOrderChanged = !isEqual(loadedColumns.order, order);

    return hasVisibilityChanged || hasWidthsChanged || hasOrderChanged;
  }, [visibility, widths, order]);

  useEffect(() => {
    if (!isConfigLoaded) {
      return;
    }

    if (!hasConfigChanged()) {
      return;
    }

    saveConfig();
  }, [visibility, widths, order, saveConfig, isConfigLoaded, hasConfigChanged]);

  return {
    loadedConfig,
  };
}
